import React, { useMemo, useEffect, useLayoutEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { variance } from 'mathjs';
import { createSelector } from 'reselect';
import { DefaultTheme, Div, Icon, SideDrawer, Text } from '@pinpoint/design-kit';
import { components, charts } from '@pinpoint/onpoint-charts';

import { themeFromTag } from 'src/functions';
import { appActions, bcActions, segmentsListActions } from 'src/store/actions';
import { Switch, GridWrapper, Message, Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import usePxScores from 'src/hooks/swr/usePxScores';

const Radar = components.withFixedTitle(charts.Radar);

const selectedSegmentIdsFilter = ({ segmentsList: { selectedSegmentIds } }) => selectedSegmentIds;
const selectedClusterIdsFilter = ({ segmentsList: { selectedClusterIds } }) => selectedClusterIds;

const NO_SEGMENTS = 'NO_SEGMENTS';
const NO_PX_DATA = 'NO_PX_DATA';
const DONE = 'DONE';
const CLOSING = 'CLOSING';

const isAnySelectedSelector = createSelector(
  [selectedSegmentIdsFilter, selectedClusterIdsFilter],
  (selectedSegmentIds, selectedClusterIds) => selectedSegmentIds.length + selectedClusterIds.length > 0
);

const selectedIdsSelector = createSelector(
  [selectedSegmentIdsFilter, selectedClusterIdsFilter],
  (selectedSegmentIds, selectedClusterIds) => [...selectedSegmentIds, ...selectedClusterIds]
);

function SegmentsViewFlyoverView({
  isOpen,
  onClose,
  app: { flyoverPinStatus },
  segmentsList: { selectedSegmentIds, selectedClusterIds },
  editSegment,
  editCluster,
  setFlyoverOpenStatus,
  setFlyoverPinStatus,
}) {
  const { data: allObjects, isLoading } = usePxScores({
    segmentIds: [
      ...new Set([
        ...selectedSegmentIds.map(({ segmentId }) => segmentId),
        ...selectedClusterIds.map(({ segmentId }) => segmentId),
      ]),
    ],
    config: { shouldFetch: isOpen },
  });
  const isClosing = useMemo(
    () => !allObjects && !isLoading && !selectedSegmentIds.length && !selectedClusterIds.length,
    [allObjects, isLoading, selectedSegmentIds, selectedClusterIds]
  );
  const [showRange, setShowRange] = useState(false);
  const isAnySelected = useSelector(isAnySelectedSelector);
  const selectedIds = useSelector(selectedIdsSelector);
  const viewColumns = useMemo(
    () => [
      {
        Header: 'Trait',
        accessor: 'label',
        cellJustify: 'center',
      },
      {
        Header: 'Percentile',
        accessor: (row) => row.score.toFixed(1),
        cellJustify: 'center',
      },
    ],
    []
  );
  const comparisonColumns = useMemo(
    () => [
      {
        Header: 'Trait',
        accessor: 'label',
        cellJustify: 'center',
      },
      {
        Header: 'Variance',
        accessor: (row) => row.variance.toFixed(1),
        cellJustify: 'center',
      },
    ],
    []
  );
  const data = useMemo(() => {
    if (isClosing) {
      return {
        objects: [],
        top7Labels: [],
        top7Series: [],
        top7Strength: [],
        top7Table: [],
        status: CLOSING,
      };
    }
    if (!allObjects) {
      return {
        objects: [],
        top7Labels: [],
        top7Series: [],
        top7Strength: [],
        top7Table: [],
        status: NO_SEGMENTS,
      };
    }
    const objects = [];
    allObjects
      .filter(({ id }) => selectedSegmentIds.find(({ segmentId }) => segmentId === id))
      .filter(
        ({ pxScores }) =>
          pxScores && pxScores.length >= CONSTANTS.FINGERPRINT.PX_FACTORS + CONSTANTS.FINGERPRINT.PX_FACETS
      )
      .forEach(({ pxScores, ...rest }) => {
        const pxScoresCopy = pxScores.slice();
        pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
        objects.push({
          ...rest,
          pxScores: pxScoresCopy.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted'),
        });
      });
    allObjects
      .filter(({ id }) => selectedClusterIds.find(({ segmentId }) => segmentId === id))
      .map(({ clusters }) => clusters)
      .reduce((accum, clusters) => [...accum, ...clusters], [])
      .filter(({ id }) => selectedClusterIds.find(({ clusterId }) => clusterId === id))
      .filter(
        ({ pxScores }) =>
          pxScores && pxScores.length >= CONSTANTS.FINGERPRINT.PX_FACTORS + CONSTANTS.FINGERPRINT.PX_FACETS
      )
      .forEach(({ pxScores, ...rest }) => {
        const pxScoresCopy = pxScores.slice();
        pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
        objects.push({
          ...rest,
          pxScores: pxScoresCopy.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted'),
        });
      });
    if (objects.length === 0) {
      return {
        objects,
        top7Labels: [],
        top7Series: [],
        top7Strength: [],
        top7Table: [],
        status: NO_PX_DATA,
      };
    }
    const segments = [];
    const traits = objects[0].pxScores.map(({ trait }) => trait);
    for (let i = 0; i < traits.length; i += 1) {
      const scores = objects.map(({ pxScores }) => pxScores[i].score);
      segments.push({
        trait: traits[i],
        scores,
        order: scores.length > 1 ? variance(...scores) || 0.01 : Math.abs(50 - scores[0]),
      });
    }
    segments.sort(({ order: orderA }, { order: orderB }) => orderB - orderA);
    // Isolate the labels
    const labels = segments.map(({ trait }) => trait);
    const strength = segments.map(({ order }) => order);
    const series = [];
    for (let i = 0; i < objects.length; i += 1) {
      series.push({
        name: objects[i].filter === 'POPULATION_SNAPSHOT' ? objects[i].dataset.name : objects[i].name,
        values: segments.map(({ scores }) => scores[i]),
        theme: objects[i].theme,
      });
    }
    const top7Labels = labels.slice(0, 7);
    const top7Series = series.map(({ name, theme, values }) => ({
      name,
      theme,
      values: values.slice(0, 7),
    }));
    const top7Strength = strength.slice(0, 7);
    const top7Table = showRange
      ? top7Series[0].values.map((value, index) => ({
          // NOTE: need to update scores and values here in the same manner
          // the Radar chart does to keep the chart and table values in sync
          label: value < 50 ? labels[index].lowRangeName : labels[index].highRangeName,
          score: 50 + Math.abs(50 - value),
          variance: top7Strength[index],
        }))
      : top7Series[0].values.map((value, index) => ({
          label: labels[index].trait,
          score: value,
          variance: top7Strength[index],
          itemStyle: {
            color: 'tagHit',
          },
        }));

    return {
      objects,
      top7Labels,
      top7Series,
      top7Strength,
      top7Table,
      status: DONE,
    };
  }, [allObjects, showRange, selectedSegmentIds, selectedClusterIds, isClosing]);

  useEffect(() => {
    if (isOpen) {
      setFlyoverOpenStatus(CONSTANTS.FLYOVER.OPEN);
    } else {
      setFlyoverOpenStatus(CONSTANTS.FLYOVER.CLOSED);
    }
  }, [isOpen, setFlyoverOpenStatus]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <SideDrawer
      w="30vw"
      minW="28rem"
      isOpen={isOpen}
      onClose={onClose}
      drawerStyle={{
        border: '1px solid',
        borderColor: 'secondary',
        top: DefaultTheme.application.headerHeight,
        h: `calc(100% - ${DefaultTheme.application.headerHeight})`,
        zIndex: '9',
        shadow: flyoverPinStatus === CONSTANTS.FLYOVER.PINNED ? '1' : '5',
      }}
      p={{ x: '1.5rem', t: '1rem', b: '0.5rem' }}
    >
      <Div d="flex" justify="space-between" w="100%" h="2.5rem">
        <Div d="flex" m={{ x: '-0.5rem' }}>
          <Button
            buttonStyle="secondary"
            rounded="circle"
            w="2.5rem"
            h="2.4rem"
            p="0rem"
            m={{ x: '0.5rem' }}
            data-tip="Hide"
            disabled={!isAnySelected}
            onClick={() => {
              onClose();
            }}
          >
            <Icon color="white" name="RightArrow" />
          </Button>
          <Button
            buttonStyle="secondary"
            rounded="circle"
            w="2.5rem"
            h="2.4rem"
            p="0rem"
            m={{ x: '0.5rem' }}
            disabled={!isAnySelected}
            data-tip="Pin to side"
            onClick={() => {
              if (flyoverPinStatus === CONSTANTS.FLYOVER.PINNED) {
                setFlyoverPinStatus(CONSTANTS.FLYOVER.UNPINNED);
              } else {
                setFlyoverPinStatus(CONSTANTS.FLYOVER.PINNED);
              }
            }}
          >
            <Icon
              color="white"
              name="PushPin"
              transform={flyoverPinStatus === CONSTANTS.FLYOVER.PINNED ? 'rotate(-45deg)' : 'rotate(0deg)'}
            />
          </Button>
        </Div>
      </Div>
      <Switch status={data.status}>
        <Div
          value={DONE}
          d="flex"
          flexDir="column-reverse"
          justify="space-between"
          w="100%"
          h="calc(100% - 3.5rem)"
          m={{ t: '1rem' }}
        >
          <Div w="calc(30vw - 3rem)" m={{ x: 'auto' }}>
            {selectedIds.length === 1 ? (
              <GridWrapper
                m="0rem"
                hasData={data.top7Table.length === 7}
                data={data.top7Table}
                columns={viewColumns}
                style={{ width: '100%' }}
              />
            ) : (
              <GridWrapper
                m="0rem"
                hasData={data.top7Table.length === 7}
                data={data.top7Table}
                columns={comparisonColumns}
              />
            )}
          </Div>
          <Div w="100%" h="60%" minH="50%" flexGrow="1">
            <Radar
              title={selectedIds.length === 1 ? 'Top 7 Divergent Traits' : 'Divergent Traits'}
              labels={data.top7Labels}
              series={data.top7Series}
              relative
              showRangeControl
              showRangeCallback={setShowRange}
              showRange={showRange}
              theme={(data && data.objects && data.objects.length > 0 && data.objects[0].theme) || undefined}
              defaultTheme={
                (data && data.objects && data.objects.length > 0 && themeFromTag(data.objects[0])) || 'pinpoint'
              }
              onThemeChange={(theme) => {
                const { segmentId, datasetId, clusterId } = selectedIds[0];
                if (datasetId) {
                  editSegment(segmentId, { theme });
                } else if (clusterId) {
                  editCluster(segmentId, clusterId, { theme });
                }
              }}
              options={{
                title: {
                  show: false,
                },
                radar: {
                  radius: selectedSegmentIds.length === 1 ? '72%' : selectedSegmentIds.length === 2 ? '61%' : '50%',
                  center: [
                    '50%',
                    selectedSegmentIds.length === 1 ? '55%' : selectedSegmentIds.length === 2 ? '46%' : '42%',
                  ],
                  name: {
                    textStyle: {
                      fontSize: 15,
                      fontWeight: 200,
                    },
                  },
                  nameGap: 20,
                  label: {
                    fontSize: 15,
                    fontWeight: 200,
                  },
                },
                series: {
                  label: {
                    fontSize: 15,
                    fontWeight: 200,
                  },
                },
              }}
            />
          </Div>
        </Div>
        <Div value={NO_SEGMENTS}>
          <Message />
        </Div>
        <Div value={NO_PX_DATA} h="calc(100% - 3.5rem)" d="flex" align="center">
          <Message opacity="0.4" type="info" />
          <Div
            zIndex="99"
            bg="gray100"
            d="flex"
            p={{ x: '1.5rem', y: '1.25rem' }}
            m={{ l: '1rem' }}
            align="flex-start"
            justify="center"
            rounded="md"
            textColor="tabSelected"
            textSize="heading"
            border="1px solid"
            borderColor="tabSelected"
            w="100%"
          >
            <Text textAlign="center">{`Under ${CONSTANTS.MIN_SEGMENT_MATCHES} matches; analytics unavailable.`}</Text>
          </Div>
        </Div>
        <Div value={CLOSING} />
      </Switch>
    </SideDrawer>
  );
}

const mapSegmentsViewFlyoverStateToProps = ({ segmentsList, app }, otherProps) => ({
  app,
  segmentsList,
  ...otherProps,
});

const mapSegmentsViewFlyoverDispatchToProps = (dispatch) => ({
  reset: () => dispatch(bcActions.reset()),
  setFlyoverOpenStatus: (...params) => dispatch(appActions.setFlyoverOpenStatus(...params)),
  setFlyoverPinStatus: (...params) => dispatch(appActions.setFlyoverPinStatus(...params)),
  editSegment: (...params) => dispatch(segmentsListActions.editSegment(...params)),
  editCluster: (...params) => dispatch(segmentsListActions.editCluster(...params)),
});

SegmentsViewFlyoverView.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  segmentsList: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        pxScores: PropTypes.arrayOf(PropTypes.shape({})),
        dataset: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      })
    ).isRequired,
    selectedSegmentIds: PropTypes.arrayOf(
      PropTypes.shape({
        segmentId: PropTypes.number,
        datasetId: PropTypes.number,
      })
    ).isRequired,
    selectedClusterIds: PropTypes.arrayOf(
      PropTypes.shape({
        segmentId: PropTypes.number,
        clusterId: PropTypes.number,
      })
    ).isRequired,
  }).isRequired,
  app: PropTypes.shape({
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
    }),
    flyoverPinStatus: PropTypes.string.isRequired,
  }).isRequired,
  setFlyoverOpenStatus: PropTypes.func.isRequired,
  setFlyoverPinStatus: PropTypes.func.isRequired,
  editSegment: PropTypes.func.isRequired,
  editCluster: PropTypes.func.isRequired,
};

export const SegmentsViewFlyover = connect(
  mapSegmentsViewFlyoverStateToProps,
  mapSegmentsViewFlyoverDispatchToProps
)(withRouter(SegmentsViewFlyoverView));
